import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Chip from 'core-system/Chip'
import TextField from 'core-system/TextField'
import FlexContainer from 'core-system/FlexContainer'
import { NumberInput, NumberFormatter } from 'core-system/Input/NumberInput'
import FormattingUtils from 'shared/FormattingUtils'
import palette from 'core-system/Themes/palette'
import { ReimbursementReceiptDetails } from 'redux/reimbursements/reimbursementsTypes'
import {
  eligibleBudgetKey,
  formatBudgetCycleRange,
  isAllocationInputInvalid,
  BudgetRow,
  getBudgetChipVariant,
} from '../utils'

const BudgetRowWrapper = styled(FlexContainer)`
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const RowMain = styled(FlexContainer)`
  flex: 1;
  min-width: 0;
  gap: 1rem;
  flex-wrap: wrap;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    width: 100%;
  }
`

const BudgetChip = styled(Chip)`
  min-width: 11rem;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    min-width: 0;
  }
`

export interface ReimbursementBudgetRowProps {
  row: BudgetRow
  receipt: ReimbursementReceiptDetails
  budgetAllocations: Record<string, string>
  onBudgetAllocationsChange: (next: Record<string, string>) => void
}

const ReimbursementBudgetRow = React.memo(
  ({
    row,
    receipt,
    budgetAllocations,
    onBudgetAllocationsChange,
  }: ReimbursementBudgetRowProps) => {
    const eligibleBudget = row.locked
      ? undefined
      : receipt.eligibleBudgets?.find((eb) => eligibleBudgetKey(eb) === row.key)
    const raw = budgetAllocations[row.key] ?? ''
    const invalid =
      eligibleBudget != null
        ? isAllocationInputInvalid(raw, eligibleBudget.available)
        : false
    const valueDollars = parseFloat(raw) || 0

    const allocationContent = row.locked ? (
      <TextField
        value={(row.amountCents / 100).toFixed(2)}
        disabled
        style={{
          width: '10rem',
          fontWeight: 600,
          backgroundColor: palette.grey.grey100,
          color: palette.text.primary,
        }}
      />
    ) : (
      <FlexContainer flexDirection='column'>
        <NumberInput
          padding='0.5rem'
          onValueChange={(dollarVal) =>
            onBudgetAllocationsChange({
              ...budgetAllocations,
              [row.key]: (dollarVal ?? 0).toFixed(2),
            })
          }
          value={valueDollars}
          incrementVal={1}
          maxWidth='10rem'
          hasIncrements={false}
        >
          <NumberFormatter
            decimalScale={2}
            fontVariant='body1'
            placeholder='0.00'
          />
        </NumberInput>
        {invalid && eligibleBudget != null && (
          <Text
            variant='body2'
            textColor={palette.error.red500}
            style={{ marginTop: '0.25rem' }}
          >
            Max {FormattingUtils.formatDollar(eligibleBudget.available)}
          </Text>
        )}
      </FlexContainer>
    )

    return (
      <BudgetRowWrapper alignItems='center'>
        <BudgetChip variant={getBudgetChipVariant(row.budgetType)}>
          {row.label}
        </BudgetChip>
        <RowMain alignItems='center' justifyContent='space-between'>
          <Text variant='body1'>
            {formatBudgetCycleRange(row.cycleStart, row.cycleEnd)}
            {row.available != null && (
              <span style={{ fontWeight: 600 }}>
                {' '}
                Available: {FormattingUtils.formatDollar(row.available)}
              </span>
            )}
          </Text>
          {allocationContent}
        </RowMain>
      </BudgetRowWrapper>
    )
  }
)

ReimbursementBudgetRow.displayName = 'ReimbursementBudgetRow'

export default ReimbursementBudgetRow
